import React from 'react';
import { useDashboardLayout } from '../hooks/useResponsiveLayout';

interface DashboardLayoutProps {
  children: React.ReactNode;
  sidebar?: React.ReactNode;
  header?: React.ReactNode;
  sidebarWidth?: number;
  collapsedSidebarWidth?: number;
  headerHeight?: number;
  autoCollapseSidebar?: boolean;
  className?: string;
  title?: string;
  subtitle?: string;
}

export function DashboardLayout({
  children,
  sidebar,
  header,
  sidebarWidth = 256,
  collapsedSidebarWidth = 64,
  headerHeight = 64,
  autoCollapseSidebar = true,
  className = '',
  title,
  subtitle
}: DashboardLayoutProps) {
  const {
    isMobile,
    closeSidebar,
    layoutStyles,
  } = useDashboardLayout({
    sidebarWidth,
    collapsedSidebarWidth,
    headerHeight,
    autoCollapseSidebar,
  });

  return (
    <div className={`min-h-screen bg-slate-900 ${className}`}>
      {/* Header */}
      {header && (
        <header 
          className="fixed top-0 left-0 right-0 z-30 bg-slate-800 border-b border-slate-700"
          style={{ height: headerHeight }}
        >
          {header}
        </header>
      )}

      {/* Page title */}
      {(title || subtitle) && (
        <div className="px-6 pt-6 pb-4" style={{ marginTop: header ? headerHeight : 0 }}>
          {title && <h1 className="text-2xl font-bold text-white">{title}</h1>}
          {subtitle && <p className="mt-1 text-sm text-slate-400">{subtitle}</p>}
        </div>
      )}

      {/* Sidebar */}
      {sidebar && (
        <>
          <aside
            className={`
              fixed top-0 left-0 z-40 h-full bg-slate-800 border-r border-slate-700
              ${isMobile ? 'shadow-lg' : ''}
            `}
            style={{
              ...layoutStyles.sidebar,
              top: header ? headerHeight : 0,
              height: header ? `calc(100vh - ${headerHeight}px)` : '100vh',
            }}
          >
            {sidebar}
          </aside>

          {/* Mobile overlay */}
          <div
            className="fixed inset-0 z-30 bg-black bg-opacity-50 lg:hidden"
            style={layoutStyles.overlay}
            onClick={closeSidebar}
          />
        </>
      )}

      {/* Main content */}
      <main
        className="transition-all duration-300 ease-in-out"
        style={{
          ...layoutStyles.content,
          paddingTop: header ? headerHeight : 0,
        }}
      >
        {children}
      </main>
    </div>
  );
}

// Industrial-grade centered content shell
export function DashboardContentShell({
  desktopWidthPct = Number(import.meta.env.VITE_DASHBOARD_DESKTOP_WIDTH_PCT ?? 70),
  children,
}: {
  desktopWidthPct?: number;
  children: React.ReactNode;
}) {
  const clamped = Math.max(40, Math.min(95, desktopWidthPct));
  return (
    <div className="min-h-screen bg-slate-900">
      <div
        className="mx-auto w-full px-3 sm:px-6 py-4 sm:py-6 space-y-4 sm:space-y-6"
        style={{
          maxWidth: `min(1600px, ${clamped}vw)`,
        }}
      >
        {children}
      </div>
    </div>
  );
}

// Dashboard page wrapper with consistent padding and spacing
interface DashboardPageProps {
  children: React.ReactNode;
  title?: string;
  subtitle?: string;
  actions?: React.ReactNode;
  className?: string;
}

export function DashboardPage({
  children,
  title,
  subtitle,
  actions,
  className = ''
}: DashboardPageProps) {
  return (
    <div className={`p-4 sm:p-6 ${className}`}>
      {/* Page header */}
      {(title || subtitle || actions) && (
        <div className="mb-4 sm:mb-6">
          <div className="flex items-center justify-between">
            <div>
              {title && (
                <h1 className="text-xl sm:text-2xl font-bold text-white">{title}</h1>
              )}
              {subtitle && (
                <p className="mt-1 text-sm text-slate-400">{subtitle}</p>
              )}
            </div>
            {actions && (
              <div className="flex items-center space-x-3">
                {actions}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Page content */}
      <div className="space-y-4 sm:space-y-6">
        {children}
      </div>
    </div>
  );
}

// KPI Bar - Industrial metrics strip
interface KPIBarProps {
  metrics: Array<{
    label: string;
    value: string | number;
    unit?: string;
    status?: 'good' | 'warning' | 'critical';
    icon?: React.ReactNode;
  }>;
}

const STATUS_COLORS = {
  good: 'text-emerald-400',
  warning: 'text-amber-400',
  critical: 'text-red-400',
};

export function KPIBar({ metrics }: KPIBarProps) {
  return (
    <div className="bg-slate-800 border border-slate-700 rounded-lg p-3 sm:p-4">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
        {metrics.map((metric, idx) => (
          <div key={idx} className="flex items-center space-x-2">
            {metric.icon && <div className="text-slate-400">{metric.icon}</div>}
            <div>
              <div className="text-xs text-slate-500 uppercase tracking-wide">{metric.label}</div>
              <div className={`text-lg sm:text-xl font-bold ${metric.status ? STATUS_COLORS[metric.status] : 'text-white'}`}>
                {metric.value}{metric.unit && <span className="text-sm ml-1">{metric.unit}</span>}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Dashboard section with consistent styling
interface DashboardSectionProps {
  children: React.ReactNode;
  title?: string;
  subtitle?: string;
  actions?: React.ReactNode;
  className?: string;
}

export function DashboardSection({
  children,
  title,
  subtitle,
  actions,
  className = ''
}: DashboardSectionProps) {
  return (
    <div className={`bg-slate-800 border border-slate-700 rounded-lg ${className}`}>
      {/* Section header */}
      {(title || subtitle || actions) && (
        <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-slate-700">
          <div className="flex items-center justify-between">
            <div>
              {title && (
                <h2 className="text-base sm:text-lg font-semibold text-white">{title}</h2>
              )}
              {subtitle && (
                <p className="mt-1 text-sm text-slate-400">{subtitle}</p>
              )}
            </div>
            {actions && (
              <div className="flex items-center space-x-2">
                {actions}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Section content */}
      <div className="p-4 sm:p-6">
        {children}
      </div>
    </div>
  );
}

// Dashboard widget container
interface DashboardWidgetProps {
  children: React.ReactNode;
  title?: string;
  subtitle?: string;
  icon?: React.ReactNode;
  actions?: React.ReactNode;
  loading?: boolean;
  error?: string;
  className?: string;
}

export function DashboardWidget({
  children,
  title,
  subtitle,
  icon,
  actions,
  loading = false,
  error,
  className = ''
}: DashboardWidgetProps) {
  return (
    <div className={`bg-slate-800 border border-slate-700 rounded-lg ${className}`}>
      {/* Widget header */}
      {(title || subtitle || icon || actions) && (
        <div className="px-4 py-3 border-b border-slate-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              {icon && (
                <div className="text-slate-400">
                  {icon}
                </div>
              )}
              <div>
                {title && (
                  <h3 className="text-sm font-medium text-white">{title}</h3>
                )}
                {subtitle && (
                  <p className="text-xs text-slate-400">{subtitle}</p>
                )}
              </div>
            </div>
            {actions && (
              <div className="flex items-center space-x-1">
                {actions}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Widget content */}
      <div className="p-4">
        {loading ? (
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center h-32 text-red-400">
            <p className="text-sm">{error}</p>
          </div>
        ) : (
          children
        )}
      </div>
    </div>
  );
}

// Dashboard stats row
interface DashboardStatsProps {
  children: React.ReactNode;
  className?: string;
}

export function DashboardStats({ children, className = '' }: DashboardStatsProps) {
  return (
    <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 ${className}`}>
      {children}
    </div>
  );
}

// Dashboard content area with sidebar toggle
interface DashboardContentProps {
  children: React.ReactNode;
  sidebarToggle?: React.ReactNode;
  className?: string;
}

export function DashboardContent({
  children,
  sidebarToggle,
  className = ''
}: DashboardContentProps) {
  return (
    <div className={`flex-1 ${className}`}>
      {/* Mobile sidebar toggle */}
      {sidebarToggle && (
        <div className="lg:hidden mb-4">
          {sidebarToggle}
        </div>
      )}
      
      {children}
    </div>
  );
}